import { KEYBOARD_KEYS, KEYBOARD_STATE_KEY } from '../../constants';
import { action, computed, observable } from 'mobx';
import { KeyboardEvents } from '../../utils/accessibility/KeyboardEvents';
import { ChildReferences } from '../../utils/accessibility/ChildReferences';
import { RefObject } from 'react';

type TOniItemSelectFn = (
    event: React.SyntheticEvent<HTMLElement>,
    item: unknown,
    index: number
) => void;

type TResetFn = () => void;

interface DropdownProps {
    items: Array<unknown>;
    pageKeyTraverseSize?: number;
    isMultiSelect?: boolean;
    hasSearch?: boolean;
    filterSearchInputRef?: any;
    resetFn?: TResetFn;
    onItemSelect: TOniItemSelectFn;
}

export class DropdownEventHandler {
    keyboardEvents: KeyboardEvents<HTMLButtonElement>;
    isMultiSelect: boolean = false;
    hasSearch: boolean = false;
    filterSearchInputRef: RefObject<HTMLInputElement> = null;

    @observable.ref items: Array<any> = [];
    @observable.ref isOpen: boolean = false;
    @observable.ref focusedIndexRef: number = -1;
    @observable.ref focusHeader: boolean | undefined;

    private pageKeyTraverseSize: number;
    private onItemSelect: TOniItemSelectFn;
    private resetFn: TResetFn;

    constructor(dropdownProps: DropdownProps) {
        this.isMultiSelect = dropdownProps.isMultiSelect || false;
        this.hasSearch = dropdownProps.hasSearch || false;
        this.items = dropdownProps.items;
        this.filterSearchInputRef = dropdownProps.filterSearchInputRef;
        this.pageKeyTraverseSize = dropdownProps.pageKeyTraverseSize || 3;
        this.onItemSelect = dropdownProps.onItemSelect;
        this.initializeKeyboardEvents();
        this.resetFn = dropdownProps.resetFn;
    }

    @computed
    get isHeaderFocused(): boolean {
        const isFocused = this.focusHeader === true;
        return isFocused;
    }

    set isHeaderFocused(state: boolean) {
        this.focusHeader = state;
        // We only want to isHeaderFocused to be true for a short period of time
        // The Focus gets confused when multiple dropdowns have isHeaderFocused set to true
        // This setTimeout allows the focus to be set to the correct dropdown
        setTimeout(() => (this.focusHeader = false), 200);
    }

    @computed
    get focusedIndex(): number {
        return this.focusedIndexRef;
    }

    set focusedIndex(index: number) {
        this.focusedIndexRef = index;
    }

    @computed
    get itemCount() {
        return this.items.length;
    }

    @computed
    get childReferences(): ChildReferences<HTMLLIElement> {
        return new ChildReferences({
            name: 'Dropdown',
            childCount: this.itemCount,
            debug: false,
        });
    }

    @action
    updateItems = (items: Array<unknown>) => {
        this.items = items;
    };

    @action
    clickNestedComponent = (index: number) => {
        const childRef = this.childReferences.childReferences[index];
        (childRef.firstElementChild as HTMLElement)?.click();
    };

    handleItemSelect = (event: React.KeyboardEvent<HTMLElement>) => {
        const item = this.items[this.focusedIndex];
        if (item?.component) {
            this.items[this.focusedIndex].component.props.onKeyDown(
                (event as unknown) as KeyboardEvent
            );
        } else {
            this.onItemSelect(event, item, this.focusedIndex);
        }

        if (!this.isMultiSelect) {
            this.closeDropdown();
        }
    };

    @action
    initializeKeyboardEvents() {
        this.keyboardEvents = new KeyboardEvents({
            name: 'Dropdown',
            debug: true,
            defaults: {
                printable: {
                    down: {
                        preventDefault: false,
                        stopPropagation: false,
                    },
                },
                accept: {
                    down: {
                        preventDefault: true,
                        stopPropagation: true,
                    },
                },
                navigation: {
                    down: {
                        preventDefault: true,
                        stopPropagation: true,
                    },
                },
                invalid: {
                    down: {
                        preventDefault: true,
                        stopPropagation: true,
                    },
                    up: {
                        preventDefault: true,
                        stopPropagation: true,
                    },
                },
            },
        });

        // KEY EVENTS
        this.keyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.SPACE,
            'down',
            (event: React.KeyboardEvent<HTMLElement>) => {
                if (this.isOpen) {
                    this.handleItemSelect(event);
                } else {
                    this.openDropdown();
                }
            }
        );

        this.keyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.ESCAPE,
            'down',
            (_: React.KeyboardEvent<HTMLElement>) => {
                if (this.isOpen) {
                    this.closeDropdown();
                }
            }
        );

        this.keyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.TAB,
            'down',
            (_: React.KeyboardEvent) => {
                // This ensures that the tab focus works in the search input
                // Everywhere else will close the dropdown and move to the next filter
                if (this.focusedIndex !== -1) {
                    this.closeDropdown();
                }
            }
        );

        this.keyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.ENTER,
            'down',
            (event: React.KeyboardEvent<HTMLElement>) => {
                if (this.isOpen) {
                    this.handleItemSelect(event);
                } else {
                    this.openDropdown();
                }
            }
        );
        this.keyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.DOWN_ARROW,
            'down',
            (_: React.KeyboardEvent) => {
                if (this.isOpen) {
                    // Make sure to focus the search input when the last item is active
                    if (this.hasSearch) {
                        if (this.focusedIndex === this.itemCount - 1) {
                            this.focusedIndex = -1;
                            this.filterSearchInputRef.current.focus();
                            return;
                        }
                    }

                    this.focusedIndex =
                        (this.focusedIndex + 1) % this.itemCount;
                } else {
                    this.openDropdown();
                }
            }
        );
        this.keyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.UP_ARROW,
            'down',
            (_: React.KeyboardEvent) => {
                if (this.isOpen) {
                    // Make sure to focus the search input when the first item is active
                    if (this.hasSearch) {
                        if (this.focusedIndex === 0) {
                            this.focusedIndex = -1;
                            this.filterSearchInputRef.current.focus();
                            return;
                        }
                    }

                    this.focusedIndex =
                        this.focusedIndex === 0
                            ? this.itemCount - 1
                            : this.focusedIndex - 1;
                } else {
                    this.openDropdown();
                }
            }
        );
        this.keyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.PAGE_DOWN,
            'down',
            () => {
                if (this.isOpen) {
                    if (this.focusedIndex === this.itemCount - 1) {
                        this.focusedIndex = 0;
                    } else if (
                        this.focusedIndex + this.pageKeyTraverseSize >
                        this.itemCount - 1
                    ) {
                        this.focusedIndex = this.itemCount - 1;
                    } else {
                        this.focusedIndex =
                            (this.focusedIndex + this.pageKeyTraverseSize) %
                            this.itemCount;
                    }
                }
            }
        );
        this.keyboardEvents.bindKeyEvent(KEYBOARD_KEYS.PAGE_UP, 'down', () => {
            if (this.isOpen) {
                if (
                    this.focusedIndex - this.pageKeyTraverseSize < 0 &&
                    this.focusedIndex !== 0
                ) {
                    this.focusedIndex = 0;
                } else if (this.focusedIndex - this.pageKeyTraverseSize < 0) {
                    this.focusedIndex = this.itemCount - 1;
                } else {
                    this.focusedIndex =
                        this.focusedIndex - this.pageKeyTraverseSize;
                }
            }
        });
        this.keyboardEvents.bindKeyEvent(KEYBOARD_KEYS.HOME, 'down', () => {
            if (this.isOpen) {
                this.focusedIndex = 0;
            }
        });
        this.keyboardEvents.bindKeyEvent(KEYBOARD_KEYS.END, 'down', () => {
            if (this.isOpen) {
                this.focusedIndex = this.itemCount - 1;
            }
        });

        this.keyboardEvents.bindStateEvent(
            KEYBOARD_STATE_KEY.POST_NAVIGATION,
            'down',
            (_: React.KeyboardEvent<HTMLElement>) => {
                this.childReferences.focusChild(this.focusedIndex);
            }
        );
        this.keyboardEvents.bindStateEvent(
            KEYBOARD_STATE_KEY.ACCEPT,
            'down',
            null,
            {
                preventDefault: true,
                stopPropagation: true,
            }
        );
        this.keyboardEvents.bindStateEvent(
            KEYBOARD_STATE_KEY.ACCEPT,
            'down',
            (event: React.KeyboardEvent<HTMLElement>) => {
                event.preventDefault();
                if (!this.isOpen) {
                    this.openDropdown();
                } else if (
                    this.isOpen &&
                    this.itemCount > 0 &&
                    this.focusedIndex >= 0
                ) {
                    const item = this.items[this.focusedIndex];
                    this.onItemSelect(event, item, this.focusedIndex);
                }
            }
        );
    }

    toggleOpen = (event: React.ChangeEvent<HTMLElement>) => {
        event.stopPropagation();
        if (this.isOpen) {
            this.closeDropdown();
        } else {
            this.openDropdown();
        }
    };

    @action
    closeDropdown = () => {
        this.isOpen = false;
        this.isHeaderFocused = true;
        this.focusedIndex = -1;

        // Reset the DropdownEventHandler to a desired state on close
        // Allows for custom behavior at the point of implementation
        if (this.resetFn) {
            this.resetFn();
        }
    };

    searchInputRefInterval = () => {
        const intervalFn = setInterval(() => {
            const searchInput = this.filterSearchInputRef;
            if (searchInput?.current) {
                searchInput.current.focus();
                clearInterval(intervalFn);
            }
        }, 200);
    };

    @action
    openDropdown = () => {
        this.isOpen = true;
        this.isHeaderFocused = false;

        // When we have a search input we need to poll the searchInputRef that was passed on instantiation
        // To ensure that the search input is focussed when the dropdown is opened
        if (this.hasSearch) {
            this.searchInputRefInterval();
        } else {
            this.focusedIndex = 0;
        }
    };
}



// WEBPACK FOOTER //
// ./src/Components/Dropdown/DropdownEventHandler.ts